import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { fetchCalendar } from '../dailyRewards.service';

import { CalendarDayVariant } from '../components/CalendarDay';

import { queryKeys } from 'hooks/queries/query-keys';

import {
  toUTCTimestamp,
  getCurrentUTCMonth,
  getCurrentUTCYear,
  mapDayToVariant,
  trimTrailingOtherMonthRows,
} from '../dailyRewards.utils';

import { CALENDAR_ROW_SIZE } from '../constants';

export type CalendarDayData = {
  day: number;
  label: string;
  variant: CalendarDayVariant;
  key: string;
  rewardImage?: string;
  rewardLabel?: string;
  vipMultiplier?: string;
};

export const useDailyRewardsCalendarQuery = (year?: number, month?: number, columns = CALENDAR_ROW_SIZE) => {
  const targetYear = year ?? getCurrentUTCYear();
  const targetMonth = month ?? getCurrentUTCMonth();

  const {
    data: response,
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.dailyRewards.calendar(targetYear, targetMonth),
    queryFn: () => fetchCalendar(targetYear, targetMonth),
  });

  const calendarDays = useMemo((): CalendarDayData[] => {
    if (!response) return [];

    const days = response.days;

    // On mobile (columns < 7), show only current_month days
    // On desktop (7 columns), trim trailing rows that are entirely other-month padding
    const filteredDays =
      columns < CALENDAR_ROW_SIZE ? days.filter((d) => d.current_month) : trimTrailingOtherMonthRows(days);

    return filteredDays.map((day) => ({
      day: day.day,
      label: day.label,
      variant: mapDayToVariant(day),
      key: `${day.date}`,
      rewardImage: day.reward?.vip_image_override || day.reward?.image_override || day.reward?.prize.icon_url,
      rewardLabel: day.reward?.prize.name,
      vipMultiplier: day.reward?.vip_multiplier && Number(day.reward.vip_multiplier) > 1 ? day.reward.vip_multiplier : undefined,
    }));
  }, [response, columns]);

  return {
    calendarDays,
    claimAvailable: response?.claim_available ?? false,
    nextClaimDate: response?.next_claim_date ? toUTCTimestamp(response.next_claim_date) : null,
    todayClaimsRemaining: response?.today_claims_remaining ?? null,
    minRmvRequired: response?.min_rmv_required ?? '0',
    minTokenWorthRequired: response?.min_token_worth_required ?? '0',
    userEligibility: response?.user_eligibility ?? null,
    isLoading,
    error,
  };
};
